import { saveAs } from 'file-saver';

import { CustomerFileWithDomainType } from '../../types/customerFile';

export const downloadFile = (blob: Blob, fileName: string) => {
	const blobUrl = URL.createObjectURL(blob);

	saveAs(blobUrl, fileName);

	URL.revokeObjectURL(blobUrl);
};

export const getFileBlob = async ({
	url,
	accessToken,
	selectedCustomerFileId,
}: {
	url: string;
	accessToken?: string;
	selectedCustomerFileId?: CustomerFileWithDomainType['id'];
}): Promise<Blob> => {
	const headers = new Headers();
	if (accessToken) {
		headers.append('Authorization', `Bearer ${accessToken}`);
	}

	if (selectedCustomerFileId) {
		headers.append('customer-file-id', selectedCustomerFileId);
	}

	const result = await fetch(url, {
		headers,
		method: 'GET',
	});

	if (!result.ok) {
		const message = await result.text();

		if (message.includes("The document isn't anymore into the storage upload.")) {
			throw new Error('Le document a déjà été synchronisé.');
		}

		throw new Error('Erreur lors de la récupération du fichier.');
	}

	const documentBlob = await result.blob();

	return documentBlob;
};

export const deleteFile = async ({
	url,
	accessToken,
	selectedCustomerFileId,
}: {
	url: string;
	accessToken?: string;
	selectedCustomerFileId?: CustomerFileWithDomainType['id'];
}): Promise<boolean> => {
	const headers = new Headers();
	if (accessToken) {
		headers.append('Authorization', `Bearer ${accessToken}`);
	}

	if (selectedCustomerFileId) {
		headers.append('customer-file-id', selectedCustomerFileId);
	}

	const result = await fetch(url, {
		headers,
		method: 'DELETE',
	});

	if (!result.ok) {
		throw new Error('Erreur lors de la suppression du fichier.');
	}

	return result.ok;
};

export const uploadFileWithFormData = async ({
	accessToken,
	file,
	uri,
	selectedCustomerFileId,
}: {
	accessToken: string;
	file: File;
	uri: URL;
	selectedCustomerFileId?: CustomerFileWithDomainType['id'];
}): Promise<void> => {
	const headers = new Headers();
	headers.append('Authorization', `Bearer ${accessToken}`);

	if (selectedCustomerFileId) {
		headers.append('customer-file-id', selectedCustomerFileId);
	}

	const body = new FormData();
	body.append('file', file);

	const response = await fetch(uri, {
		method: 'POST',
		headers,
		body,
	});

	if (!response.ok) {
		throw new Error('Unable to upload file');
	}
};

export const uploadFilesWithFormData = async ({
	accessToken,
	files,
	selectedCustomerFileId,
	uri,
}: {
	accessToken: string;
	files: File[];
	selectedCustomerFileId?: CustomerFileWithDomainType['id'];
	uri: URL;
}): Promise<void> => {
	const headers = new Headers();
	headers.append('Authorization', `Bearer ${accessToken}`);
	if (selectedCustomerFileId) headers.append('customer-file-id', selectedCustomerFileId);

	const formData = new FormData();
	files.forEach((file) => {
		formData.append('files', file);
	});

	const response = await fetch(uri, {
		method: 'POST',
		headers,
		body: formData,
	});

	if (!response.ok) {
		throw new Error('Unable to upload files');
	}
};

export const uploadFileWithFormDataAndReturningResponse = async <T>({
	accessToken,
	file,
	selectedCustomerFileId,
	uri,
}: {
	accessToken: string;
	file: File;
	selectedCustomerFileId: CustomerFileWithDomainType['id'];
	uri: URL;
}): Promise<undefined | T> => {
	const headers = new Headers();
	headers.append('Authorization', `Bearer ${accessToken}`);
	headers.append('customer-file-id', selectedCustomerFileId);

	const body = new FormData();
	body.append('file', file);

	const response = await fetch(uri, {
		method: 'POST',
		headers,
		body,
	});
	if (response.ok) return response.json() as T;
	throw new Error('Unable to upload file');
};

export const uploadFileAndReturnDataOrError = async <T, E>({
	accessToken,
	file,
	selectedCustomerFileId,
	uri,
}: {
	accessToken: string;
	file: File;
	selectedCustomerFileId: CustomerFileWithDomainType['id'];
	uri: URL;
}): Promise<{ data?: T; error?: E }> => {
	const headers = new Headers();
	headers.append('Authorization', `Bearer ${accessToken}`);
	headers.append('customer-file-id', selectedCustomerFileId);

	const body = new FormData();
	body.append('file', file);

	const response = await fetch(uri, {
		method: 'POST',
		headers,
		body,
	});

	if (response.ok) {
		const data = (await response.json()) as T;

		return { data };
	}

	const error = (await response.json()) as E;

	return { error };
};

export const formatFileSize = (fileSize: number) => {
	const sizes = ['Octets', 'Ko', 'Mo', 'Go', 'To'];

	if (fileSize === 0) return '0 Octet';

	const i = Math.floor(Math.log(fileSize) / Math.log(1000));
	const formattedSize = (fileSize / 1000 ** i).toFixed(2);

	return `${formattedSize} ${sizes[i]}`;
};

export const openFileInNewTab = (file: File) => {
	const fileURL = URL.createObjectURL(file);

	window.open(fileURL, '_blank', 'noopener,noreferrer');

	/**
	 * Revoke the object URL after some time to prevent memory leaks.
	 *
	 * Chromium specific behavior:
	 *  - If the URL is revoked too early, the user will not be able to download
	 *    the file from the built-in viewer.
	 *  - We keep the URL alive for 5 minutes to ensure the file is fully loaded
	 *    and the user has enough time to download it before it becomes invalid.
	 */
	setTimeout(() => URL.revokeObjectURL(fileURL), 300000);
};
